import type { AuditLog } from './AuditLogs'
import { formatDate as formatSharedDate, formatDateTime } from '@/utils/formatters'

export function getEventIcon(event: string): string {
  switch (event) {
    case 'created':
      return 'pi pi-plus-circle'
    case 'updated':
      return 'pi pi-pencil'
    case 'deleted':
      return 'pi pi-trash'
    case 'restored':
      return 'pi pi-undo'
    case 'viewed':
      return 'pi pi-eye'
    default:
      return 'pi pi-circle'
  }
}

export function getEventSeverity(event: string): 'success' | 'info' | 'warning' | 'danger' {
  switch (event) {
    case 'created':
      return 'success'
    case 'updated':
      return 'info'
    case 'deleted':
      return 'danger'
    case 'restored':
      return 'warning'
    case 'viewed':
      return 'info'
    default:
      return 'info'
  }
}

export function getUserDisplayName(user: AuditLog['user']): string {
  if (!user) return 'System'
  if (user.forename && user.surname) {
    return `${user.forename} ${user.surname}`
  }
  if (user.username) return user.username
  if (user.email) return user.email
  if (user.name) return user.name
  return 'Unknown User'
}

export function formatHistoryDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`

  return formatDateTime(date, 'dd/MM/yy HH:mm')
}

export function getFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    title: 'Title',
    active: 'Status',
    completed_at: 'Completed Date',
    plan_id: 'Plan',
    due_date: 'Due Date',
    state: 'Status',
    color_hex: 'Colour',
    kpi_area: 'KPI Area',
    details: 'Details',
    single_retailer_id: 'Retailer',
    multiple_retailers: 'Retailers',
    group_id: 'Group',
  }
  return labels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

export function formatFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return 'Not set'
  if (value === '') return 'Empty'

  if (field === 'active') {
    return value === true || value === 1 || value === '1' ? 'Complete' : 'In Progress'
  }

  if (field === 'state') {
    const stateLabels: Record<string, string> = {
      pending: 'Open',
      active: 'Active',
      complete: 'Complete',
    }
    return stateLabels[String(value)] || String(value)
  }

  if (field === 'due_date' || field === 'completed_at') {
    if (typeof value === 'string') {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return formatSharedDate(date, 'dd/MM/yy')
      }
    }
  }

  if (field === 'color_hex' && typeof value === 'string') {
    return value
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return 'None'
    return value.map((v) => (typeof v === 'object' && v?.label ? v.label : String(v))).join(', ')
  }

  if (typeof value === 'object' && value !== null) {
    if ('label' in value) return String(value.label)
    if ('title' in value) return String(value.title)
    if ('name' in value) return String(value.name)
  }

  return String(value)
}

export function getEventLabel(event: string, auditableType?: string | null): string {
  const isAction = auditableType?.includes('Action')
  const isPlan = auditableType?.includes('Plan')

  if (isAction) {
    switch (event) {
      case 'created':
        return 'Action Created'
      case 'updated':
        return 'Action Updated'
      case 'deleted':
        return 'Action Deleted'
      case 'restored':
        return 'Action Restored'
      case 'viewed':
        return 'Action Viewed'
      default:
        return event.charAt(0).toUpperCase() + event.slice(1)
    }
  }

  if (isPlan) {
    switch (event) {
      case 'created':
        return 'Plan Created'
      case 'updated':
        return 'Plan Updated'
      case 'deleted':
        return 'Plan Deleted'
      case 'restored':
        return 'Plan Restored'
      case 'viewed':
        return 'Plan Viewed'
      default:
        return event.charAt(0).toUpperCase() + event.slice(1)
    }
  }

  return event.charAt(0).toUpperCase() + event.slice(1)
}

export function getEventDescription(log: AuditLog, context?: 'plan' | 'action'): string | null {
  const isAction = log.auditable_type?.includes('Action')
  const isPlan = log.auditable_type?.includes('Plan')
  const contextIsPlan = context === 'plan'
  const contextIsAction = context === 'action'

  if (log.event === 'created') {
    if (isAction) {
      const actionTitle = log.new_values?.title
      if (contextIsPlan) {
        return actionTitle
          ? `Action "${actionTitle}" was added to this plan.`
          : 'An action was added to this plan.'
      }
      return actionTitle ? `Action "${actionTitle}" was created.` : 'This action was created.'
    }
    if (isPlan) {
      return 'This plan was created.'
    }
  }

  if (log.event === 'updated') {
    if (isAction) {
      const actionTitle = log.new_values?.title || log.old_values?.title
      const changedFields = log.changed_attributes || []
      if (changedFields.length === 1) {
        const field = changedFields[0]
        if (contextIsPlan) {
          return actionTitle
            ? `Action "${actionTitle}" had its ${getFieldLabel(field).toLowerCase()} updated.`
            : `An action's ${getFieldLabel(field).toLowerCase()} was updated.`
        }
        return actionTitle
          ? `Action "${actionTitle}" had its ${getFieldLabel(field).toLowerCase()} updated.`
          : `Action ${getFieldLabel(field).toLowerCase()} was updated.`
      }
      if (contextIsPlan) {
        return actionTitle
          ? `Action "${actionTitle}" was updated.`
          : 'An action in this plan was updated.'
      }
      return actionTitle ? `Action "${actionTitle}" was updated.` : 'This action was updated.'
    }
    if (isPlan) {
      const changedFields = log.changed_attributes || []
      if (changedFields.length === 1) {
        const field = changedFields[0]
        return `Plan ${getFieldLabel(field).toLowerCase()} was updated.`
      }
      return 'This plan was updated.'
    }
  }

  if (log.event === 'deleted') {
    if (isAction) {
      const actionTitle = log.old_values?.title
      if (contextIsPlan) {
        return actionTitle
          ? `Action "${actionTitle}" was removed from this plan.`
          : 'An action was removed from this plan.'
      }
      return actionTitle ? `Action "${actionTitle}" was deleted.` : 'This action was deleted.'
    }
    if (isPlan) {
      return 'This plan was deleted.'
    }
  }

  if (log.event === 'viewed') {
    if (isAction) {
      if (contextIsPlan) {
        return 'An action in this plan was viewed.'
      }
      return 'This action was viewed.'
    }
    if (isPlan) {
      return 'This plan was viewed.'
    }
  }

  return null
}
